<template>
  <v-dialog
    :value="value"
    max-width="780"
    scrollable
    persistent
    :dark="darkEnabled"
    @input="$emit('input', $event)"
  >
    <v-card max-height="850" :dark="darkEnabled" :class="cardClass">
      <v-card-title class="pr-cfg-head pa-4">
        <v-btn icon small aria-label="Fechar" @click="fechar">
          <v-icon small>fas fa-times</v-icon>
        </v-btn>
        <span class="pr-cfg-title text-h6 flex-grow-1 text-center"
          >Editar Regras</span
        >
        <span class="pr-cfg-head-spacer" />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4 pt-6 pr-cfg-card-text">
        <v-overlay
          :value="loading"
          absolute
          z-index="3"
          opacity="0.55"
          :dark="darkEnabled"
        />
        <p v-if="loadError" class="error--text">{{ loadError }}</p>

        <div class="pr-section-label mb-2 ml-3">
          <v-icon small :color="sectionIconColor" class="mr-3"
            >fas fa-award</v-icon
          >
          <span>Nome do programa</span>
        </div>
        <FormField v-model="form.program_name" :disabled="loading || saving" />
        <FormSwitch
          class="mt-2"
          v-model="form.program_active"
          label="Programa ativo"
          help="Permite que os clientes usem o programa de recompensas."
          :disabled="loading || saving"
        />

        <div class="d-flex align-center flex-wrap mt-6 mb-2">
          <div class="pr-section-label mb-0 mr-4">
            <v-icon small :color="sectionIconColor" class="mx-3"
              >fas fa-eye</v-icon
            >
            <span>Configurações de acúmulo e resgate</span>
          </div>
          <v-btn-toggle
            v-model="aba"
            mandatory
            dense
            class="pr-aba-toggle ml-auto"
            :class="{ 'pr-aba-toggle--dark': darkEnabled }"
            :disabled="loading || saving"
          >
            <v-btn small value="acumulo">Acúmulo</v-btn>
            <v-btn small value="resgate">Resgate</v-btn>
          </v-btn-toggle>
        </div>

        <v-divider class="my-5" />

        <div v-show="aba === 'acumulo'" class="mt-2">
          <div class="pr-cat-box pa-4">
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="pr-section-label mb-2 ml-3">
                  <v-icon small :color="catBoxIconColor" class="mr-1"
                    >fas fa-exchange-alt</v-icon
                  >
                  <span>R$ 1,00 equivale a (pontos)</span>
                </div>
                <FormField
                  v-model="form.pontos_por_real"
                  :disabled="loading || saving"
                  hint="Quantidade de pontos por real gasto nas compras elegíveis."
                  :persistenthint="true"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="pr-section-label mb-2 ml-3">
                  <v-icon small :color="catBoxIconColor" class="mr-1"
                    >fas fa-calendar-alt</v-icon
                  >
                  <span>Validade dos pontos (dias)</span>
                </div>
                <FormField
                  v-model="form.validade_pontos_dias"
                  typeField="number"
                  :disabled="loading || saving"
                  hint="Deixe em branco para não aplicar expiração por data nos pontos."
                  :persistenthint="true"
                />
              </v-col>
            </v-row>
          </div>
          <FormSwitch
            class="mt-4"
            v-model="form.habilitado_acumulo"
            label="Habilitado para pontuar"
            help="Permite que os clientes ganhem pontos nas compras elegíveis."
            :disabled="loading || saving"
          />
          <div class="pr-cat-box mt-1 pa-4">
            <div class="pr-section-label mb-2">
              <v-icon small :color="catBoxIconColor" class="mr-1"
                >fas fa-layer-group</v-icon
              >
              <span>Categorias elegíveis (acúmulo)</span>
            </div>
            <p class="pr-hint mb-3">
              Caso nenhuma categoria seja selecionada, o acúmulo será validado
              para todos os produtos da loja.
            </p>
            <FormAutocomplete
              placeholder="Selecionar categorias…"
              :options="categorias"
              v-model="form.setor_ids_acumulo"
              multiple
              chips
              :clearable="true"
              :disabled="loading || saving || categoriasLoading"
              :loading="categoriasLoading"
            />
          </div>
        </div>

        <div v-show="aba === 'resgate'" class="mt-2">
          <div class="pr-section-label mb-2 ml-3">
            <v-icon small :color="sectionIconColor" class="mr-3"
              >fas fa-money-bill-wave</v-icon
            >
            <span>Cada 1 ponto equivale a (R$)</span>
          </div>
          <FormField
            v-model="form.reais_por_ponto"
            :money="moneyMask"
            :disabled="loading || saving"
            hint="Valor em reais que cada ponto vale no momento do resgate."
            :persistenthint="true"
          />
          <div class="pr-cat-box pa-4">
            <p class="pr-subheading mt-2 mb-1">
              <v-icon small :color="catBoxIconColor" class="mr-2"
                >fas fa-receipt</v-icon
              >
              <span>Limites em valor do cupom (R$)</span>
            </p>
            <v-row dense class="mt-1">
              <v-col cols="12" md="6">
                <div class="pr-section-label mb-2 ml-3">
                  <v-icon small :color="catBoxIconColor" class="mr-1"
                    >fas fa-arrow-alt-circle-down</v-icon
                  >
                  <span>Valor mínimo do cupom</span>
                </div>
                <FormField
                  v-model="form.valor_minimo_cupom"
                  :disabled="loading || saving"
                  clearable
                  hint="Vazio = sem valor mínimo. Ex.: 50 ou 50,00"
                  :persistenthint="true"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="pr-section-label mb-2 ml-3">
                  <v-icon small :color="catBoxIconColor" class="mr-1"
                    >fas fa-arrow-alt-circle-up</v-icon
                  >
                  <span>Valor máximo do cupom</span>
                </div>
                <FormField
                  v-model="form.valor_maximo_cupom"
                  :disabled="loading || saving"
                  clearable
                  hint="Vazio = sem valor máximo. Ex.: 150 ou 150,00"
                  :persistenthint="true"
                />
              </v-col>
            </v-row>
          </div>
          <div class="pr-cat-box mt-4 pa-4">
            <p class="pr-subheading mt-2 mb-1">
              <v-icon small :color="catBoxIconColor" class="mr-2"
                >fas fa-shopping-cart</v-icon
              >
              <span>Limites em valor do pedido (R$)</span>
            </p>
            <v-row dense class="mt-1">
              <v-col cols="12" md="6">
                <div class="pr-section-label mb-2 ml-3">
                  <v-icon small :color="catBoxIconColor" class="mr-1"
                    >fas fa-arrow-alt-circle-down</v-icon
                  >
                  <span>Valor mínimo do pedido</span>
                </div>
                <FormField
                  v-model="form.valor_minimo_pedido"
                  :disabled="loading || saving"
                  clearable
                  hint="Vazio = sem valor mínimo de pedido. Ex.: 50 ou 50,00"
                  :persistenthint="true"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="pr-section-label mb-2 ml-3">
                  <v-icon small :color="catBoxIconColor" class="mr-1"
                    >fas fa-arrow-alt-circle-up</v-icon
                  >
                  <span>Valor máximo do pedido</span>
                </div>
                <FormField
                  v-model="form.valor_maximo_pedido"
                  :disabled="loading || saving"
                  clearable
                  hint="Vazio = sem valor máximo de pedido. Ex.: 150 ou 150,00"
                  :persistenthint="true"
                />
              </v-col>
            </v-row>
          </div>
          <div class="pr-cat-box mt-4 pa-4">
            <p class="pr-subheading mt-4 mb-1">
              <v-icon small :color="catBoxIconColor" class="mr-2"
                >fas fa-star</v-icon
              >
              <span>Limites em pontos por pedido</span>
            </p>
            <v-row dense class="mt-1">
              <v-col cols="12" md="6">
                <div class="pr-section-label mb-2 ml-3">
                  <v-icon small :color="catBoxIconColor" class="mr-1"
                    >fas fa-long-arrow-alt-down</v-icon
                  >
                  <span>Pontos mínimos de resgate</span>
                </div>
                <FormField
                  v-model="form.pontos_minimos_resgate"
                  :disabled="loading || saving"
                  clearable
                  hint="Vazio = sem mínimo de pontos. Use ponto ou vírgula para decimais."
                  :persistenthint="true"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="pr-section-label mb-2 ml-3">
                  <v-icon small :color="catBoxIconColor" class="mr-1"
                    >fas fa-long-arrow-alt-up</v-icon
                  >
                  <span>Pontos máximos de resgate</span>
                </div>
                <FormField
                  v-model="form.pontos_maximos_resgate"
                  :disabled="loading || saving"
                  clearable
                  hint="Vazio = sem máximo em pontos."
                  :persistenthint="true"
                />
              </v-col>
            </v-row>
          </div>
          <div class="pr-section-label mb-2 ml-3 mt-4">
            <v-icon small :color="sectionIconColor" class="mr-3"
              >fas fa-calendar-check</v-icon
            >
            <span>Validade do cupom gerado (dias)</span>
          </div>
          <FormField
            v-model="form.validade_cupom_dias"
            typeField="number"
            :disabled="loading || saving"
          />
          <FormSwitch
            class="mt-1"
            v-model="form.habilitado_resgate"
            label="Ativo para resgate"
            help="Permite que os clientes troquem pontos por cupons no checkout."
            :disabled="loading || saving"
          />
          <div class="pr-cat-box mt-4 pa-4">
            <div class="pr-section-label mb-2">
              <v-icon small :color="catBoxIconColor" class="mr-1"
                >fas fa-layer-group</v-icon
              >
              <span>Categorias elegíveis (resgate)</span>
            </div>
            <p class="pr-hint mb-3">
              Caso nenhuma categoria seja selecionada, o resgate será validado
              para todos os produtos da loja. Apenas uma categoria pode ser
              vinculada ao resgate.
            </p>
            <FormAutocomplete
              placeholder="Selecionar categoria…"
              :options="categorias"
              v-model="form.setor_id_resgate"
              :clearable="true"
              chips
              :disabled="loading || saving || categoriasLoading"
              :loading="categoriasLoading"
            />
          </div>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <FormButton
          text="Cancelar"
          type="error"
          :disabled="saving"
          @click="fechar"
        />
        <FormButton
          text="Salvar"
          type="submissao"
          :loading="saving"
          @click="salvar"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiWeb from "@/service/api-web";
import FormAutocomplete from "@/components/form/FormAutocomplete";
import FormButton from "@/components/form/FormButton";
import FormField from "@/components/form/FormField";
import FormSwitch from "@/components/form/FormSwitch";
import { SETOR_FOLHA_LOAD } from "@/store/actions/setor";
import { mapGetters } from "vuex";

function trimApiNumber(s) {
  if (s == null || s === "") return "";
  const n = Number(String(s).replace(",", "."));
  if (!Number.isFinite(n)) return String(s);
  return String(n);
}

function moneyToApi(val) {
  if (val == null || val === "") return "0";
  let s = String(val)
    .replace(/\s/g, "")
    .replace(/R\$\s?/gi, "");
  if (s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? String(n) : "0";
}

function brMoneyFromApi(s, maxFrac) {
  if (s == null || s === "") return "";
  const n = Number(String(s).replace(",", "."));
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: maxFrac
  });
}

function pointsToApi(val) {
  const s = String(val == null ? "" : val)
    .replace(/\s/g, "")
    .replace(",", ".")
    .trim();
  return s;
}

function stripMoneyDisplay(val) {
  if (val == null) return "";
  return String(val)
    .replace(/\s/g, "")
    .replace(/R\$\s?/gi, "")
    .trim();
}

function optionalMoneyToApi(val) {
  const stripped = stripMoneyDisplay(val);
  if (stripped === "" || stripped === "," || stripped === ".") {
    return null;
  }
  let s = stripped;
  if (s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    s = s.replace(/\./g, "");
  }
  const n = Number(s);
  if (!Number.isFinite(n) || n === 0) {
    return null;
  }
  return String(n);
}

function optionalPointsToApi(val) {
  const s = pointsToApi(val);
  if (s === "") return null;
  const n = Number(s);
  if (Number.isFinite(n) && n === 0) {
    return null;
  }
  return s;
}

export default {
  name: "ProgramaRecompensaConfigModal",
  components: {
    FormAutocomplete,
    FormButton,
    FormField,
    FormSwitch
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    idfornecedor: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(["darkEnabled"]),
    cardClass() {
      return {
        "pr-cfg-card": true,
        "pr-cfg-card--dark": this.darkEnabled
      };
    },
    sectionIconColor() {
      return this.darkEnabled ? "grey lighten-2" : "grey darken-2";
    },
    catBoxIconColor() {
      return this.darkEnabled ? "blue lighten-2" : "primary";
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadError: "",
      categoriasLoading: false,
      categorias: [],
      aba: "acumulo",
      form: {
        program_name: "",
        program_active: true,
        pontos_por_real: "1",
        validade_pontos_dias: "365",
        habilitado_acumulo: true,
        setor_ids_acumulo: [],
        reais_por_ponto: "",
        valor_minimo_cupom: "",
        valor_maximo_cupom: "",
        valor_minimo_pedido: "",
        valor_maximo_pedido: "",
        pontos_minimos_resgate: "",
        pontos_maximos_resgate: "",
        validade_cupom_dias: "365",
        habilitado_resgate: true,
        setor_id_resgate: null
      },
      moneyMask: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        suffix: "",
        precision: 2,
        masked: false
      }
    };
  },
  watch: {
    value(v) {
      if (v) {
        this.aba = "acumulo";
        this.loadError = "";
        this.ensureCategorias();
        this.carregarConfig();
      }
    }
  },
  methods: {
    fechar() {
      this.$emit("input", false);
    },
    ensureCategorias() {
      if (this.categorias.length || this.categoriasLoading) {
        return;
      }
      this.categoriasLoading = true;
      this.$store
        .dispatch(SETOR_FOLHA_LOAD)
        .then(data => {
          this.categorias = (data || []).map(c => ({
            ...c,
            text: c.rotulo,
            value: c.id
          }));
        })
        .catch(() => {
          this.$vueOnToast.pop("error", "Falha ao buscar categorias");
        })
        .finally(() => {
          this.categoriasLoading = false;
        });
    },
    aplicarResposta(data) {
      const rg = data.regras_gerais || {};
      const ra = data.regras_acumulo || {};
      const rr = data.regras_resgate || {};

      const vd = ra.validade_pontos_dias;
      const validadePontosStr =
        vd == null || vd === "" || Number.isNaN(Number(vd))
          ? ""
          : String(Math.floor(Number(vd)));

      const setorRes = Array.isArray(rr.setor_ids) ? rr.setor_ids : [];
      const setorResgateUm = setorRes.length ? setorRes[0] : null;

      const maxCupRaw =
        rr.valor_maximo_cupom != null
          ? String(rr.valor_maximo_cupom).trim()
          : "";
      const maxPedRaw =
        rr.valor_maximo_pedido != null
          ? String(rr.valor_maximo_pedido).trim()
          : "";
      const maxPtsRaw =
        rr.pontos_maximos_resgate != null
          ? String(rr.pontos_maximos_resgate).trim()
          : "";

      this.form = {
        program_name: rg.program_name != null ? String(rg.program_name) : "",
        program_active: Boolean(rg.active),
        pontos_por_real: trimApiNumber(
          ra.pontos_por_real != null ? ra.pontos_por_real : "1"
        ),
        validade_pontos_dias: validadePontosStr,
        habilitado_acumulo: Boolean(ra.habilitado),
        setor_ids_acumulo: Array.isArray(ra.setor_ids) ? [...ra.setor_ids] : [],
        reais_por_ponto: brMoneyFromApi(rr.reais_por_ponto, 4),
        valor_minimo_cupom:
          rr.valor_minimo_cupom != null &&
          String(rr.valor_minimo_cupom).trim() !== ""
            ? brMoneyFromApi(rr.valor_minimo_cupom, 2)
            : "",
        valor_maximo_cupom:
          maxCupRaw !== "" ? brMoneyFromApi(rr.valor_maximo_cupom, 2) : "",
        valor_minimo_pedido:
          rr.valor_minimo_pedido != null &&
          String(rr.valor_minimo_pedido).trim() !== ""
            ? brMoneyFromApi(rr.valor_minimo_pedido, 2)
            : "",
        valor_maximo_pedido:
          maxPedRaw !== "" ? brMoneyFromApi(rr.valor_maximo_pedido, 2) : "",
        pontos_minimos_resgate:
          rr.pontos_minimos_resgate != null &&
          String(rr.pontos_minimos_resgate).trim() !== ""
            ? trimApiNumber(rr.pontos_minimos_resgate)
            : "",
        pontos_maximos_resgate:
          maxPtsRaw !== "" ? trimApiNumber(rr.pontos_maximos_resgate) : "",
        validade_cupom_dias:
          rr.validade_cupom_dias != null && rr.validade_cupom_dias !== ""
            ? String(Math.floor(Number(rr.validade_cupom_dias)))
            : "0",
        habilitado_resgate: Boolean(rr.habilitado),
        setor_id_resgate: setorResgateUm
      };
    },
    carregarConfig() {
      const fid = this.idfornecedor;
      if (!fid) {
        this.loadError = "Fornecedor não identificado.";
        return;
      }
      this.loading = true;
      this.loadError = "";
      ApiWeb.api
        .get(`/api/v4/programa-recompensa/config/${fid}`)
        .then(res => {
          this.aplicarResposta(res.data || {});
        })
        .catch(err => {
          const msg =
            (err.response && err.response.data && err.response.data.err) ||
            err.message ||
            "Não foi possível carregar as regras.";
          this.loadError = typeof msg === "string" ? msg : "Erro ao carregar.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    montarPayload() {
      const nome = String(this.form.program_name || "").trim();
      const validadePontosRaw = String(
        this.form.validade_pontos_dias || ""
      ).trim();
      const validadePontosNum = parseInt(validadePontosRaw, 10);
      const regrasAcumulo = {
        setor_ids: Array.isArray(this.form.setor_ids_acumulo)
          ? [...this.form.setor_ids_acumulo]
          : [],
        pontos_por_real:
          String(this.form.pontos_por_real || "")
            .replace(",", ".")
            .trim() || "0",
        habilitado: Boolean(this.form.habilitado_acumulo)
      };
      if (
        validadePontosRaw !== "" &&
        Number.isInteger(validadePontosNum) &&
        validadePontosNum >= 1
      ) {
        regrasAcumulo.validade_pontos_dias = validadePontosNum;
      }

      const setorResgate = this.form.setor_id_resgate
        ? [this.form.setor_id_resgate]
        : [];

      const regrasResgate = {
        setor_ids: setorResgate,
        reais_por_ponto: moneyToApi(this.form.reais_por_ponto),
        habilitado: Boolean(this.form.habilitado_resgate),
        pontos_minimos_resgate: optionalPointsToApi(
          this.form.pontos_minimos_resgate
        ),
        pontos_maximos_resgate: optionalPointsToApi(
          this.form.pontos_maximos_resgate
        ),
        valor_minimo_cupom: optionalMoneyToApi(this.form.valor_minimo_cupom),
        valor_maximo_cupom: optionalMoneyToApi(this.form.valor_maximo_cupom),
        valor_minimo_pedido: optionalMoneyToApi(this.form.valor_minimo_pedido),
        valor_maximo_pedido: optionalMoneyToApi(this.form.valor_maximo_pedido),
        validade_cupom_dias: Math.max(
          0,
          parseInt(String(this.form.validade_cupom_dias || "0"), 10) || 0
        )
      };

      return {
        regras_gerais: {
          program_name: nome,
          active: Boolean(this.form.program_active)
        },
        regras_acumulo: regrasAcumulo,
        regras_resgate: regrasResgate
      };
    },
    salvar() {
      const nome = String(this.form.program_name || "").trim();
      if (!nome) {
        this.$vueOnToast.pop("error", "Informe o nome do programa.");
        return;
      }
      const ptsReal = Number(
        String(this.form.pontos_por_real || "").replace(",", ".")
      );
      if (!Number.isFinite(ptsReal) || ptsReal <= 0) {
        this.$vueOnToast.pop(
          "error",
          "Informe quantos pontos o cliente ganha por R$ 1,00 (valor maior que zero)."
        );
        return;
      }
      const reaisPonto = Number(moneyToApi(this.form.reais_por_ponto));
      if (!Number.isFinite(reaisPonto) || reaisPonto <= 0) {
        this.$vueOnToast.pop(
          "error",
          "Informe o valor em R$ equivalente a 1 ponto no resgate (maior que zero)."
        );
        return;
      }
      const vdRaw = String(this.form.validade_pontos_dias || "").trim();
      if (vdRaw !== "") {
        const n = parseInt(vdRaw, 10);
        if (!Number.isInteger(n) || n < 1) {
          this.$vueOnToast.pop(
            "error",
            "A validade dos pontos em dias deve ser um número inteiro ≥ 1, ou deixe em branco para não expirar."
          );
          return;
        }
      }

      const minCupOpt = optionalMoneyToApi(this.form.valor_minimo_cupom);
      const maxCupOpt = optionalMoneyToApi(this.form.valor_maximo_cupom);
      if (maxCupOpt != null) {
        const maxCup = Number(maxCupOpt);
        if (!Number.isFinite(maxCup) || maxCup <= 0) {
          this.$vueOnToast.pop(
            "error",
            "O valor máximo do cupom, se informado, deve ser maior que zero."
          );
          return;
        }
        if (minCupOpt != null) {
          const minCup = Number(minCupOpt);
          if (Number.isFinite(minCup) && minCup > maxCup) {
            this.$vueOnToast.pop(
              "error",
              "O valor mínimo do cupom não pode ser maior que o máximo."
            );
            return;
          }
        }
      }
      if (minCupOpt != null) {
        const minCup = Number(minCupOpt);
        if (!Number.isFinite(minCup) || minCup < 0) {
          this.$vueOnToast.pop(
            "error",
            "O valor mínimo do cupom, se informado, deve ser um número ≥ 0."
          );
          return;
        }
      }

      const minPedOpt = optionalMoneyToApi(this.form.valor_minimo_pedido);
      const maxPedOpt = optionalMoneyToApi(this.form.valor_maximo_pedido);
      if (maxPedOpt != null) {
        const maxPed = Number(maxPedOpt);
        if (!Number.isFinite(maxPed) || maxPed <= 0) {
          this.$vueOnToast.pop(
            "error",
            "O valor máximo do pedido, se informado, deve ser maior que zero."
          );
          return;
        }
        if (minPedOpt != null) {
          const minPed = Number(minPedOpt);
          if (Number.isFinite(minPed) && minPed > maxPed) {
            this.$vueOnToast.pop(
              "error",
              "O valor mínimo do pedido não pode ser maior que o máximo."
            );
            return;
          }
        }
      }
      if (minPedOpt != null) {
        const minPed = Number(minPedOpt);
        if (!Number.isFinite(minPed) || minPed < 0) {
          this.$vueOnToast.pop(
            "error",
            "O valor mínimo do pedido, se informado, deve ser um número ≥ 0."
          );
          return;
        }
      }

      const minPtsOpt = optionalPointsToApi(this.form.pontos_minimos_resgate);
      const maxPtsOpt = optionalPointsToApi(this.form.pontos_maximos_resgate);
      if (minPtsOpt != null) {
        const minPts = Number(minPtsOpt);
        if (!Number.isFinite(minPts) || minPts < 0) {
          this.$vueOnToast.pop(
            "error",
            "Os pontos mínimos de resgate, se informados, devem ser ≥ 0."
          );
          return;
        }
      }
      if (maxPtsOpt != null) {
        const maxPts = Number(maxPtsOpt);
        if (!Number.isFinite(maxPts) || maxPts < 0) {
          this.$vueOnToast.pop(
            "error",
            "Os pontos máximos de resgate, se informados, devem ser ≥ 0."
          );
          return;
        }
        if (minPtsOpt != null) {
          const minPts = Number(minPtsOpt);
          if (Number.isFinite(minPts) && minPts > maxPts) {
            this.$vueOnToast.pop(
              "error",
              "Os pontos mínimos de resgate não podem ser maiores que os máximos."
            );
            return;
          }
        }
      }

      const fid = this.idfornecedor;
      if (!fid) {
        this.$vueOnToast.pop("error", "Fornecedor não identificado.");
        return;
      }

      this.saving = true;
      ApiWeb.api
        .put(`/api/v4/programa-recompensa/config/${fid}`, this.montarPayload())
        .then(() => {
          this.$vueOnToast.pop("success", "Regras salvas com sucesso.");
          this.$emit("saved");
          this.$emit("input", false);
        })
        .catch(err => {
          const msg =
            (err.response && err.response.data && err.response.data.err) ||
            err.message ||
            "Não foi possível salvar.";
          this.$vueOnToast.pop(
            "error",
            typeof msg === "string" ? msg : "Erro ao salvar."
          );
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style scoped>
.pr-cfg-head {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.pr-cfg-title {
  font-weight: 600;
}

.pr-cfg-card--dark .pr-cfg-title {
  color: rgba(255, 255, 255, 0.95);
}

.pr-cfg-head-spacer {
  display: inline-block;
  width: 36px;
  flex-shrink: 0;
}

.pr-section-label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
}

.pr-cfg-card--dark .pr-section-label {
  color: rgba(255, 255, 255, 0.72);
}

.pr-subheading {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  letter-spacing: 0.02em;
}

.pr-cfg-card--dark .pr-subheading {
  color: rgba(255, 255, 255, 0.88);
}

.pr-aba-toggle .v-btn {
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.04em;
}

.pr-aba-toggle--dark {
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 4px;
}

.pr-aba-toggle--dark .v-btn {
  color: rgba(255, 255, 255, 0.88) !important;
}

.pr-aba-toggle--dark .v-btn:not(.v-btn--active) {
  background: rgba(255, 255, 255, 0.06) !important;
}

.pr-aba-toggle--dark .v-btn--active {
  background: rgba(100, 181, 246, 0.35) !important;
  color: #fff !important;
}

.pr-cat-box {
  border: 1px solid rgba(25, 118, 210, 0.35);
  border-radius: 8px;
  background: rgba(25, 118, 210, 0.02);
}

.pr-cfg-card--dark .pr-cat-box {
  border-color: rgba(100, 181, 246, 0.45);
  background: rgba(18, 24, 32, 0.65);
}

.pr-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
}

.pr-cfg-card--dark .pr-hint {
  color: rgba(255, 255, 255, 0.65);
}

.pr-cfg-card-text {
  position: relative;
}

.pr-cfg-card--dark ::v-deep .v-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
</style>
